import { defaultLang, supportedLangs } from '@geartracker/types';

import {
  addAssetRecoveryBreadcrumb,
  isRecoverableAssetLoadError,
  requestAssetRecovery,
  retryRecoverableAssetLoad,
} from 'src/utils/asset-recovery';
import { getErrorMessage } from 'src/utils/error-message';
import { loadTranslations, resolveSupportedLang } from './load-translations';

import * as Sentry from '@sentry/react';
import type { BackendModule } from 'i18next';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// ----------------------------------------------------------------------

/**
 * Set as soon as a language bundle fails to load. Every key then misses at
 * once, which would make `missingKeyHandler` report hundreds of keys that
 * exist perfectly well — burying the single event that matters, the chunk that
 * never arrived.
 *
 * Cleared again by the next bundle that does load: a failure that recovery
 * could not reload away (`changeLanguage` back to a language that works, or an
 * exhausted recovery attempt) would otherwise silence real missing keys for
 * the rest of the session.
 */
let translationBundleFailed = false;

/**
 * Loads language bundles on demand instead of baking all five into the entry
 * chunk. `read` runs at init for the detected language and again on every
 * `changeLanguage`, so callers keep using i18next normally.
 */
const lazyTranslationsBackend: BackendModule = {
  type: 'backend',
  init: () => {
    // No configuration: `loadTranslations` owns the chunk map.
  },
  read: (lng, _namespace, callback) => {
    // i18next may ask for a region-qualified tag (`nonExplicitSupportedLngs`
    // accepts `fr-FR` as `fr`), so resolve before hitting the chunk map.
    const resolvedLng = resolveSupportedLang(lng);

    retryRecoverableAssetLoad(
      () => loadTranslations(resolvedLng),
      (error) =>
        addAssetRecoveryBreadcrumb(
          'translation bundle load failed, retrying',
          { lng: resolvedLng, errorMessage: getErrorMessage(error) },
          'warning',
        ),
    )
      .then((bundle) => {
        translationBundleFailed = false;
        callback(null, bundle);
      })
      .catch((error: unknown) => {
        translationBundleFailed = true;

        if (isRecoverableAssetLoadError(error)) {
          // A language bundle is one of our own chunks, so it goes missing for
          // the same reasons any other one does: a stale deploy, a poisoned
          // cache. `requestAssetRecovery` (service-worker/cache wipe + reload)
          // is what fixes those — and it has to run, because `fallbackLng` is
          // false: without a bundle the user faces an interface made entirely
          // of raw translation keys.
          const recoveryAction = requestAssetRecovery('i18n-chunk', error);

          // Only the failure the reload could not repair is worth reporting:
          // that is the one that actually leaves the user in front of raw keys.
          if (recoveryAction === 'exhausted') {
            Sentry.captureMessage('Translation bundle failed to load', {
              level: 'warning',
              tags: { feature: 'translations' },
              extra: { lng: resolvedLng, errorMessage: getErrorMessage(error) },
            });
          }
        } else {
          Sentry.captureException(error, {
            tags: { feature: 'translations' },
            extra: { lng: resolvedLng },
          });
        }

        callback(
          error instanceof Error ? error : new Error(String(error)),
          false,
        );
      });
  },
};

void i18n
  .use(lazyTranslationsBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        caches: ['localStorage'],
        // Clamp to a language we ship a bundle for. Doing it here (rather than
        // leaning on `fallbackLng`) is what keeps a session to a single chunk:
        // i18next preloads its fallback language eagerly, so an unresolved tag
        // would cost a second bundle for every non-English user.
        convertDetectedLanguage: resolveSupportedLang,
      },
      // Detection always yields a supported language and `pnpm i18n:check`
      // enforces identical key sets across the five files, so no fallback
      // bundle is needed. A key that slips through still reaches Sentry via
      // `missingKeyHandler` below.
      fallbackLng: false,
      // Load the resolved language only — never the `fr` + `fr-FR` pair.
      load: 'currentOnly',
      supportedLngs: supportedLangs,
      nonExplicitSupportedLngs: true,
      debug: false,
      ns: ['translations'],
      defaultNS: 'translations',
      saveMissing: true,
      missingKeyHandler: (
        lngs,
        _ns,
        key,
        _fallbackValue,
        _updateMissing,
        options,
      ) => {
        // A bundle that never loaded makes every key miss at once. That failure
        // is already reported above; reporting each key it takes down with it
        // only drowns it under events pointing at keys that are not missing.
        if (translationBundleFailed) {
          return;
        }

        // A `defaultValue` is NOT a reason to stay quiet, however tempting it
        // looks: every API hook resolves a backend error code that way
        // (`t(code, { defaultValue: t('error') })`), and surfacing the codes
        // the backend grew without a translation is exactly what this handler
        // is for. Filtering on `options.defaultValue` silenced five real gaps
        // in the user create/update guards — do not add that filter back; add
        // the missing key instead.
        //
        // The one legitimate opt-out is a key built from organization data,
        // which no bundle can ever hold. Those call sites say so explicitly
        // with `reportMissing: false` (see `getCategoryLabel`).
        if (options?.reportMissing === false) {
          return;
        }

        const message = `Missing translation: ${key}`;
        console.warn(message);
        Sentry.captureMessage('Missing translation', {
          extra: {
            key,
            lngs,
          },
          level: 'warning',
          tags: {
            feature: 'translations',
          },
        });
      },
      interpolation: {
        escapeValue: false,
        skipOnVariables: false,
      },
    },
    () => {
      document.documentElement.lang =
        i18n.resolvedLanguage || i18n.language || defaultLang;
    },
  );

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
